import { requestToCMS } from './requestToCMS';
import { generateStrapiQueryString, StrapiQueryOptions } from '../utils';
import { Endpoint, getStrapiEndpoint } from '../../constants';
import { StrapiError } from '../types';

const defaultQueryOptions: Partial<StrapiQueryOptions> = {
    pagination: {
        start: 0,
        limit: 1000
    },
    populate: ['*']
};

/**
 * Configuration for the get request made to the CMS.
 */
type GetConfig = {
    /*
     The endpoint to make the request to
     */
    endpoint: Endpoint;
    /*
    If just a specific resource of an endpoint needs to be fetched pass here the id
     */
    id?: string;
    /*
     Optional query options to adjust the request with filters or pagination. A default config will be applied when
     nothing is provided.
     */
    queryOptions?: Partial<StrapiQueryOptions>;
    /*
    The token to use in the request. Either the user token for logged in/user specific actions or our API token for general requests.
     */
    jwt: string;
};

/**
 * Base method to fetch data from Strapi Endpoint via GET request and the possibility to adjust the query options that
 * will be sent with the request. When no query definition is sent with this request, a default config will be added which
 * will disable pagination and populate all relation fields.
 */
export const fetchFromCMS = async <T, E extends StrapiError<T> = any>(config: GetConfig) => {
    const { id, endpoint, jwt } = config;

    if (!jwt) {
        console.error(`No JWT provided for endpoint ${endpoint}.`);
        return {
            data: null,
            error: {
                message: 'No JWT provided',
                status: 401,
                name: 'Unauthorized'
            }
        };
    }

    const queryOptions = config.queryOptions
        ? { ...defaultQueryOptions, ...config.queryOptions }
        : defaultQueryOptions;
    const idPath = id ? `/${id}` : '';

    const requestUrl = `${getStrapiEndpoint(endpoint)}${idPath}${generateStrapiQueryString(queryOptions)}`;

    return requestToCMS<T, E>({ url: requestUrl, method: 'GET', accessToken: jwt });
};
